import React, { useState, useEffect, useMemo } from 'react';
import { useTeam } from '../../../shared/context/teamContext';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import UsersFilterModule from '../../conversion-events/components/UsersFilterModule';
import Icon from '../../../shared/components/Icon';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';
import ConfirmModal from '../../analytics/mixpanel/components/ConfirmModal';

const styles = {
  primaryBg: '#ffffff',
  secondaryBg: '#DDE2EE',
  textPrimary: '#0f172a',
  textSecondary: '#666666',
  accentColor: '#4b57aa',
  borderColor: '#DDE2EE'
};

/**
 * Create/Edit Cohort Modal - filter builder + name + save/cancel
 */
const CohortModal = ({ isOpen, onClose, teamId, cohort, onSaved }) => {
  const [filters, setFilters] = useState([]);
  const [cohortName, setCohortName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isEdit = !!cohort;

  useEffect(() => {
    if (isOpen) {
      setFilters(cohort?.filters || []);
      setCohortName(cohort?.name || '');
      setError(null);
    }
  }, [isOpen, cohort]);

  const handleSave = async () => {
    const name = (cohortName || '').trim();
    if (!name) {
      setError('Please enter a cohort name');
      return;
    }
    if (!teamId) {
      setError('No team selected');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await axiosInstance.put(`/cohorts/${cohort.id}`, { name, filters });
      } else {
        await axiosInstance.post('/cohorts', { team_id: teamId, name, filters });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save cohort');
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full max-w-5xl rounded-xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'Raleway, sans-serif', backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold whitespace-nowrap" style={{ color: styles.textPrimary }}>Cohort Name:</span>
            <input
              type="text"
              placeholder="e.g. Power Users, High Value"
              value={cohortName}
              onChange={(e) => setCohortName(e.target.value)}
              className="w-64 px-3 py-2 text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4b57aa]"
              style={{
                border: `1px solid ${styles.borderColor}`,
                backgroundColor: styles.primaryBg,
                color: styles.textPrimary
              }}
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: styles.textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = styles.textPrimary;
              e.currentTarget.style.backgroundColor = styles.secondaryBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = styles.textSecondary;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ fontSize: 20, lineHeight: 1 }} aria-hidden>×</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          <UsersFilterModule
            filters={filters}
            onFiltersChange={setFilters}
            selectedSiteIds={[]}
            teamId={teamId}
          />
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 flex-shrink-0" style={{ borderTop: `1px solid ${styles.borderColor}` }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={{
              color: styles.textSecondary,
              backgroundColor: styles.primaryBg,
              border: `1px solid ${styles.borderColor}`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.secondaryBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.primaryBg;
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !(cohortName || '').trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{
              backgroundColor: styles.accentColor
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#3d4799';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.accentColor;
            }}
          >
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CohortsPage = () => {
  const { selectedTeam } = useTeam();
  const teamId = useMemo(() => {
    if (selectedTeam?._id) return selectedTeam._id;
    if (selectedTeam?.id) return selectedTeam.id;
    try {
      const td = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      return td._id || td.id || null;
    } catch {
      return null;
    }
  }, [selectedTeam]);

  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingCounts = false; // kept for compatibility with hot-reload; user count column removed
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCohort, setEditingCohort] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cohortToDelete, setCohortToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [siteId, setSiteId] = useState(null);

  useEffect(() => {
    const idy = localStorage.getItem('idy');
    setSiteId(idy || null);
  }, []);

  useEffect(() => {
    if (teamId) fetchCohorts();
  }, [teamId, siteId]);

  const fetchCohorts = async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      const params = { team_id: teamId };
      if (siteId) params.site_id = siteId;
      const res = await axiosInstance.get('/cohorts', { params });
      setCohorts(res.data?.cohorts || []);
    } catch (err) {
      console.error('Error fetching cohorts:', err);
      setError(err.response?.data?.message || 'Failed to load cohorts');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCohort(null);
    setModalOpen(true);
  };

  const openEditModal = (cohort) => {
    setEditingCohort(cohort);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCohort(null);
  };

  const formatLastEdited = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  const requestDelete = (cohort) => {
    setCohortToDelete(cohort);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!cohortToDelete || deleting) return;

    setDeleting(true);
    setError(null);
    try {
      await axiosInstance.delete(`/cohorts/${cohortToDelete.id}`);
      setDeleteModalOpen(false);
      setCohortToDelete(null);
      fetchCohorts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete cohort');
    } finally {
      setDeleting(false);
    }
  };

  if (!teamId) {
    return (
      <div className="py-12 text-center w-full" style={{ fontFamily: 'Raleway, sans-serif', color: styles.textSecondary }}>
        Please select a team to manage cohorts.
      </div>
    );
  }

  return (
    <div className="w-full" style={{ fontFamily: 'Raleway, sans-serif' }}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold" style={{ color: styles.textPrimary }}>Saved Cohorts</h2>
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
          style={{
            backgroundColor: styles.primaryBg,
            color: styles.textPrimary,
            border: `1px solid ${styles.borderColor}`
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.secondaryBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.primaryBg;
          }}
        >
          <Icon name="add_plus" size={16} />
          Add cohort
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Cohorts list */}
      <div>
        {loading ? (
          <div className="flex justify-center py-12">
            <LiquidFillLoader size={60} />
          </div>
        ) : cohorts.length === 0 ? (
          <div
            className="py-12 text-center rounded-lg"
            style={{ color: styles.textSecondary, border: `1px dashed ${styles.borderColor}` }}
          >
            <p>No cohorts yet.</p>
            <p className="text-sm mt-1">Click &quot;Add cohort&quot; to create your first cohort.</p>
          </div>
        ) : (
          <div
            className="rounded-lg overflow-hidden w-full"
            style={{ border: `1px solid ${styles.borderColor}` }}
          >
            <table className="w-full" style={{ fontFamily: 'Raleway, sans-serif' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: styles.textPrimary }}>
                    Cohort Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: styles.textPrimary }}>
                    Last Edited
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: styles.textPrimary }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {cohorts.map((cohort) => (
                  <tr
                    key={cohort.id}
                    style={{
                      backgroundColor: styles.primaryBg,
                      borderBottom: `1px solid ${styles.borderColor}`
                    }}
                  >
                    <td className="px-6 py-4 text-sm font-medium" style={{ color: styles.textPrimary }}>
                      {cohort.name}
                    </td>
                    <td className="px-6 py-4 text-sm" style={{ color: styles.textSecondary }}>
                      {formatLastEdited(cohort.updatedAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEditModal(cohort)}
                          className="p-2 rounded transition-colors"
                          style={{ color: styles.textSecondary }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = styles.textPrimary;
                            e.currentTarget.style.backgroundColor = styles.secondaryBg;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = styles.textSecondary;
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Edit"
                        >
                          <Icon name="edit_pencil" size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => requestDelete(cohort)}
                          className="p-2 rounded transition-colors"
                          style={{ color: styles.textSecondary }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#dc2626';
                            e.currentTarget.style.backgroundColor = '#fef2f2';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = styles.textSecondary;
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Delete"
                        >
                          <Icon name="delete" size={16} color="currentColor" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CohortModal
        isOpen={modalOpen}
        onClose={closeModal}
        teamId={teamId}
        cohort={editingCohort}
        onSaved={fetchCohorts}
      />

      <ConfirmModal
        open={deleteModalOpen}
        danger
        title="Delete Cohort"
        message={
          cohortToDelete?.name
            ? `Are you sure you want to delete the cohort "${cohortToDelete.name}"? This action cannot be undone.`
            : 'Are you sure you want to delete this cohort? This action cannot be undone.'
        }
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          if (deleting) return;
          setDeleteModalOpen(false);
          setCohortToDelete(null);
        }}
      />
    </div>
  );
};

export default CohortsPage;
